import React, { useState, useMemo } from "react";
import { MapPin, Plus, Trash2, Search, TrendingUp, Loader2, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { adminRefreshBus } from "../utils/adminRefreshBus";
import RefreshControl from "./admin/RefreshControl";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PincodeManagementTab = ({ auth, authAxios }) => {
  const [expandedCity, setExpandedCity] = useState(null);
  const [showDemand, setShowDemand] = useState(false);

  // Add form
  const [addPincode, setAddPincode] = useState("");
  const [addArea, setAddArea] = useState("");
  const [addCity, setAddCity] = useState("");
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState("");
  const [searchQ, setSearchQ] = useState("");

  // iter163 — auto-refresh. One scope "pincodes" covers both the main list
  // AND the demand aggregates since they come from the same tab's mutations.
  const {
    data: pincodeData,
    loading: pincodeLoading,
    refresh: refreshPincodes,
    lastUpdated,
  } = useAutoRefresh(
    async () => {
      const r = await authAxios.get(`${API_URL}/api/admin/serviceable-pincodes`);
      return r.data;
    },
    { scope: "pincodes", interval: 60_000 }
  );
  const { data: demandData, loading: demandLoading } = useAutoRefresh(
    async () => {
      const r = await authAxios.get(`${API_URL}/api/admin/pincode-demand`);
      return r.data || [];
    },
    { scope: "pincodes", interval: 60_000 }
  );
  const cities = pincodeData?.cities || [];
  const total = pincodeData?.total || 0;
  const demand = demandData || [];
  const loading = pincodeLoading || demandLoading;

  const handleAdd = async () => {
    if (!addPincode || !addCity) { setMsg("Pincode and city are required"); return; }
    setAdding(true);
    setMsg("");
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/serviceable-pincodes`, {
        pincode: addPincode.trim(), area: addArea.trim(), city: addCity.trim(),
      });
      setMsg(r.data.message);
      setAddPincode(""); setAddArea("");
      adminRefreshBus.emit("pincodes");
    } catch (err) {
      setMsg(err.response?.data?.detail || "Failed to add");
    } finally { setAdding(false); }
  };

  const handleDelete = async (pincode) => {
    if (!window.confirm(`Remove pincode ${pincode}?`)) return;
    try {
      await authAxios.delete(`${API_URL}/api/admin/serviceable-pincodes/${pincode}`);
      adminRefreshBus.emit("pincodes");
    } catch (e) { console.debug('[Pincode] Delete failed:', e.message); }
  };

  const handleDeleteCity = async (cityName) => {
    if (!window.confirm(`Remove ALL pincodes for ${cityName}?`)) return;
    try {
      await authAxios.delete(`${API_URL}/api/admin/serviceable-pincodes/city/${cityName}`);
      adminRefreshBus.emit("pincodes");
    } catch (e) { console.debug('[Pincode] City delete failed:', e.message); }
  };

  const filteredCities = useMemo(() => cities.map(c => ({
    ...c,
    pincodes: c.pincodes.filter(p =>
      !searchQ || p.pincode.includes(searchQ) || (p.area || "").toLowerCase().includes(searchQ.toLowerCase())
    ),
  })).filter(c => c.pincodes.length > 0), [cities, searchQ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-[#E83E8C]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pincode-management-tab">
      {/* iter163 — refresh chip (manual + auto-poll + focus-refresh signal) */}
      <div className="flex items-center justify-end">
        <RefreshControl
          lastUpdated={lastUpdated}
          onRefresh={() => refreshPincodes({ background: false })}
          loading={loading}
        />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-[#94a3b8] uppercase tracking-wider">Total Pincodes</p>
          <p className="text-2xl font-bold text-[#1E1B4B]" data-testid="total-pincodes">{total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-[#94a3b8] uppercase tracking-wider">Cities Served</p>
          <p className="text-2xl font-bold text-[#E83E8C]">{cities.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-[#94a3b8] uppercase tracking-wider">Demand Requests</p>
          <p className="text-2xl font-bold text-[#FF9F1C]">{demand.reduce((s, d) => s + (d.request_count || 0), 0)}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <p className="text-xs text-[#94a3b8] uppercase tracking-wider">Areas Requesting</p>
          <p className="text-2xl font-bold text-[#475569]">{demand.length}</p>
        </div>
      </div>

      {/* Add Pincode */}
      <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
        <h3 className="font-semibold text-[#1E1B4B] mb-3 flex items-center gap-2"><Plus size={16} /> Add Pincode</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs text-[#94a3b8] mb-1 block">Pincode or Range *</label>
            <input type="text" value={addPincode} onChange={e => setAddPincode(e.target.value)}
              placeholder="400001 or 400001-400010"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#E83E8C] outline-none"
              data-testid="add-pincode-input" />
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="text-xs text-[#94a3b8] mb-1 block">Area</label>
            <input type="text" value={addArea} onChange={e => setAddArea(e.target.value)}
              placeholder="e.g. Andheri"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#E83E8C] outline-none" />
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="text-xs text-[#94a3b8] mb-1 block">City *</label>
            <input type="text" value={addCity} onChange={e => setAddCity(e.target.value)}
              placeholder="e.g. Mumbai"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#E83E8C] outline-none"
              data-testid="add-city-input" />
          </div>
          <button onClick={handleAdd} disabled={adding}
            className="px-5 py-2 bg-[#E83E8C] text-white rounded-lg font-medium text-sm hover:bg-[#d63384] transition disabled:opacity-50 flex items-center gap-1.5"
            data-testid="add-pincode-btn">
            {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
          </button>
        </div>
        {msg && <p className="mt-2 text-sm text-[#475569]" data-testid="add-pincode-msg">{msg}</p>}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
        <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
          placeholder="Search pincodes..."
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#E83E8C] outline-none bg-white"
          data-testid="search-pincodes" />
      </div>

      {/* Serviceable Pincodes by City */}
      <div className="space-y-3">
        {filteredCities.map(city => (
          <div key={city.city} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Outer row is a div (not button) so inner delete <button> is valid HTML.
                Previously this triggered a React DOM-nesting hydration warning. */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setExpandedCity(expandedCity === city.city ? null : city.city)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setExpandedCity(expandedCity === city.city ? null : city.city);
                }
              }}
              className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition cursor-pointer select-none"
              data-testid={`city-${city.city.replace(/\s/g, '-').toLowerCase()}`}>
              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-[#E83E8C]" />
                <span className="font-semibold text-[#1E1B4B]">{city.city}</span>
                <span className="text-xs bg-[#E83E8C]/10 text-[#E83E8C] px-2 py-0.5 rounded-full font-medium">
                  {city.pincodes.length} pincodes
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={(e) => { e.stopPropagation(); handleDeleteCity(city.city); }}
                  className="text-red-400 hover:text-red-600 p-1 rounded transition" title="Remove all pincodes for this city">
                  <Trash2 size={14} />
                </button>
                {expandedCity === city.city ? <ChevronUp size={16} className="text-[#94a3b8]" /> : <ChevronDown size={16} className="text-[#94a3b8]" />}
              </div>
            </div>
            {expandedCity === city.city && (
              <div className="px-5 pb-4 border-t border-gray-100">
                <div className="flex flex-wrap gap-2 pt-3">
                  {city.pincodes.map(p => (
                    <span key={p.pincode} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-lg text-sm group hover:bg-red-50 transition">
                      <span className="font-mono text-[#1E1B4B]">{p.pincode}</span>
                      {p.area && p.area !== city.city && <span className="text-[#94a3b8] text-xs">({p.area})</span>}
                      <button onClick={() => handleDelete(p.pincode)}
                        className="text-[#94a3b8] hover:text-red-500 opacity-0 group-hover:opacity-100 transition ml-0.5">
                        <Trash2 size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Demand Analytics */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <button onClick={() => setShowDemand(!showDemand)}
          className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition"
          data-testid="demand-toggle">
          <div className="flex items-center gap-3">
            <TrendingUp size={16} className="text-[#FF9F1C]" />
            <span className="font-semibold text-[#1E1B4B]">Demand from Non-Serviceable Areas</span>
            <span className="text-xs bg-[#FF9F1C]/10 text-[#FF9F1C] px-2 py-0.5 rounded-full font-medium">
              {demand.length} areas
            </span>
          </div>
          {showDemand ? <ChevronUp size={16} className="text-[#94a3b8]" /> : <ChevronDown size={16} className="text-[#94a3b8]" />}
        </button>
        {showDemand && (
          <div className="px-5 pb-4 border-t border-gray-100">
            {demand.length === 0 ? (
              <p className="text-sm text-[#94a3b8] py-3">No demand data yet</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {demand.map((d, i) => (
                  <div key={d.pincode || `demand-${i}`} className="flex items-center justify-between py-2.5">
                    <div>
                      <span className="font-mono text-sm text-[#1E1B4B]">{d.pincode}</span>
                      <span className="text-[#94a3b8] text-xs ml-2">
                        {[d.city, d.district, d.state].filter(Boolean).join(", ")}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-[#FF9F1C] bg-[#FF9F1C]/10 px-2 py-0.5 rounded-full">
                        {d.request_count} requests
                      </span>
                      <button onClick={() => { setAddPincode(d.pincode); setAddCity(d.district || d.city || ""); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className="text-xs text-[#E83E8C] hover:underline font-medium"
                        data-testid={`add-demand-${d.pincode}`}>
                        + Add
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PincodeManagementTab;
